import { useMutation } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import useDataDog from '@/reporting/DataDog';
import useTakerMarkets from './useTakerMarkets';

const useDeleteOfferSchedule = () => {
  const { data: takerMarkets } = useTakerMarkets();
  const { addError } = useDataDog();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();

  return useMutation({
    mutationKey: ['deleteOfferSchedule'],
    mutationFn: async ({ offerUuid }: { offerUuid?: string | null }) => {
      if (!offerUuid) {
        throw new Error('Offer UUID is required');
      }

      const takerMarket = takerMarkets?.find((tm) => tm.offerConfig.uuid === offerUuid);

      if (takerMarket?.marketPricingType === 'PRICE_DISCOVERY') {
        return apiClient
          .patch('api/supplier-experience/offers/price-discovery', {
            json: {
              offers: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  uuid: offerUuid,
                },
              ],
              schedule: null,
            } satisfies paths['/offers/price-discovery']['patch']['requestBody']['content']['application/json'],
          })
          .json<paths['/offers/price-discovery']['patch']['responses']['204']['content']>();
      }

      if (takerMarket?.marketPricingType === 'BENCHMARK') {
        return apiClient
          .patch('api/supplier-experience/offers/benchmark', {
            json: {
              offers: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  uuid: offerUuid,
                },
              ],
              schedule: null,
            } satisfies paths['/offers/benchmark']['patch']['requestBody']['content']['application/json'],
          })
          .json<paths['/offers/benchmark']['patch']['responses']['204']['content']>();
      }

      if (takerMarket?.marketPricingType === 'STATIC') {
        return apiClient
          .patch('api/supplier-experience/offers/static', {
            json: {
              offers: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  uuid: offerUuid,
                },
              ],
              schedule: null,
            } satisfies paths['/offers/static']['patch']['requestBody']['content']['application/json'],
          })
          .json<paths['/offers/static']['patch']['responses']['204']['content']>();
      }

      throw new Error('Unsupported market pricing type');
    },
    onSuccess: (_, { offerUuid }) => {
      const takerMarket = takerMarkets?.find((tm) => tm.offerConfig.uuid === offerUuid);

      if (takerMarket) {
        refetchOneTakerMarket({
          marketUuid: takerMarket.marketUuid,
          takerId: takerMarket.takerDivisionId,
        });
      }
    },
    onError: (error, variables) => {
      addError('automation::deleteSchedule::error', {
        error,
        variables,
      });
    },
  });
};

export default useDeleteOfferSchedule;
